/**
 * The conversation, and what confirming a proposal does.
 *
 * Asking is one call. The answer comes back with intents, which are applied to
 * the curate screen's state, and proposals, which are not applied to anything:
 * they sit on screen until the scientist confirms one, and confirming calls the
 * ordinary curation routes under their name.
 *
 * `newChat` empties the panel. It does not delete a turn: the server keeps the
 * whole transcript and only changes which thread the next question is answered
 * in, so the conversation the scientist just cleared is still readable and
 * still attributable.
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { getUser } from "../lib/user";
import { toRequest } from "./proposals";

/* Until the server has answered, the panel is neither on nor off. Rendering it
   as available and then taking the box away is worse than waiting: the
   scientist starts typing a question that was never going to be sent. */
const UNKNOWN = { available: null, kind: null, reason: "" };

export function useCopilot({ runId, apply, onCurationChanged }) {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [availability, setAvailability] = useState(UNKNOWN);

  /* Asked once, not per run: what is configured is a property of the
     installation. A failure here is treated as "no copilot" rather than
     assumed working — the whole point of this call is that the panel must not
     invite a question it cannot answer. */
  useEffect(() => {
    let live = true;
    api
      .getAssistantAvailability()
      .then((state) => live && setAvailability(state))
      .catch(() =>
        live &&
        setAvailability({
          available: false,
          kind: "none",
          reason:
            "The copilot could not be reached, so it cannot answer questions about this run. Your analysis, curation and exports are unaffected.",
        })
      );
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    setMessages([]);
    setConversationId(null);
    setPending([]);
    api
      .getAssistantTranscript(runId)
      .then((data) => {
        if (!live) return;
        setMessages(data.messages);
        setConversationId(data.conversation_id || null);
      })
      .catch(() => {
        // An unreadable transcript is not a reason to withhold the panel; the
        // scientist can still ask. The failure shows when they do.
      });
    return () => {
      live = false;
    };
  }, [runId]);

  const ask = useCallback(
    async (question) => {
      const text = question.trim();
      // The panel already greys itself out; this is the second lock, for the
      // suggestion chips and anything else that calls `ask` directly.
      if (!text || busy || availability.available !== true) return;
      setBusy(true);
      setError(null);
      setMessages((was) => [...was, { role: "user", content: text, local: true }]);
      try {
        const turn = await api.askAssistant(runId, text, getUser(), conversationId);
        // The server decides the thread when we sent none -- adopt it, or the
        // next question starts yet another conversation.
        setConversationId(turn.conversation_id);
        setMessages((was) => [
          ...was,
          {
            role: "assistant",
            content: turn.answer,
            grounded: turn.grounded,
            tools_used: turn.tools_used,
          },
        ]);
        apply(turn.intents);
        setPending(turn.proposals || []);
      } catch (e) {
        setError(e);
      } finally {
        setBusy(false);
      }
    },
    [runId, apply, busy, conversationId, availability]
  );

  const newChat = useCallback(async () => {
    if (busy || starting) return;
    setStarting(true);
    setError(null);
    try {
      const { conversation_id: started } = await api.startAssistantConversation(runId);
      setConversationId(started);
      setMessages([]);
      // Proposals belong to the conversation that produced them. Leaving one
      // on screen would let it be confirmed out of the context it was made in.
      setPending([]);
    } catch (e) {
      setError(e);
    } finally {
      setStarting(false);
    }
  }, [runId, busy, starting]);

  const reject = useCallback((index) => {
    // Nothing to undo: a proposal was never applied.
    setPending((was) => was.filter((_, i) => i !== index));
  }, []);

  const confirm = useCallback(
    async (index) => {
      const request = toRequest(pending[index], getUser());
      if (!request) return;
      setBusy(true);
      setError(null);
      try {
        if (request.action === "exclude") {
          await api.exclude(
            runId,
            request.observationIds,
            request.reason,
            request.excludedBy,
            request.source
          );
        } else if (request.action === "include") {
          for (const id of request.observationIds) {
            await api.include(runId, id, request.reinstatedBy, request.source);
          }
        } else if (request.action === "acknowledge") {
          await api.acknowledgeQc(runId, request.acknowledgedBy);
        }
        setPending((was) => was.filter((_, i) => i !== index));
        await onCurationChanged?.();
      } catch (e) {
        setError(e);
      } finally {
        setBusy(false);
      }
    },
    [pending, runId, onCurationChanged]
  );

  return {
    availability,
    messages,
    pending,
    busy,
    starting,
    error,
    ask,
    confirm,
    reject,
    newChat,
    dismissError: () => setError(null),
  };
}
